import { authHeaders } from '../apiService';
import { classificationBase, classificationContextScope, classificationRequest, ClassificationContext, ClassificationRequestError } from './transport';
import { digest, SHARING_MUTATION_PREFIX } from './bridge';

export type DealRecipient = { user_id: string; name: string; email: string; access: 'edit' };
export type DealSharing = { share_id: string | null; root_contract_id: string; revision: string | null;
  can_manage: boolean; recipients: DealRecipient[] };
export type SharedDeal = { share_id: string; project_id: string; root_contract_id: string; label: string; access: 'edit' };
export type SharingCandidate = { user_id: string; name: string; email: string };
export class SharingChangeError extends Error {
  constructor(message: string, public confirmedRejected = false) { super(message); }
}

function sharePath(context: ClassificationContext, root: string) {
  if (!/^[a-f0-9]{32}$/.test(root)) throw new Error('Invalid deal reference.');
  return `/projects/${encodeURIComponent(context.project_id)}/deals/${root}/sharing`;
}
function checked(value: any, root: string): DealSharing {
  if (value?.root_contract_id !== root || !Array.isArray(value.recipients) || typeof value.can_manage !== 'boolean'
    || (value.revision !== null && !/^[a-f0-9]{32}$/.test(value.revision))
    || (value.share_id !== null && !/^cs_[a-f0-9]{32}$/.test(value.share_id))
    || value.recipients.some((r: DealRecipient) => typeof r.user_id !== 'string' || typeof r.email !== 'string' || r.access !== 'edit')) {
    throw new Error('Deal sharing information could not be verified.');
  }
  return value;
}
export async function readDealSharing(context: ClassificationContext, root: string, signal: AbortSignal) {
  return checked(await (await classificationRequest(sharePath(context, root), signal)).json(), root);
}
export async function findSharingCandidates(context: ClassificationContext, root: string, query: string, signal: AbortSignal): Promise<SharingCandidate[]> {
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(query.trim())) throw new Error('Enter a full colleague email.');
  const body = await (await classificationRequest(`${sharePath(context, root)}/candidates?query=${encodeURIComponent(query)}`, signal)).json();
  if (!Array.isArray(body.users) || body.users.some((u: SharingCandidate) => typeof u.user_id !== 'string' || typeof u.email !== 'string')) throw new Error('Organization members could not be loaded.');
  return body.users;
}
export async function readSharedDeals(signal: AbortSignal): Promise<SharedDeal[]> {
  let response: Response;
  try { response = await classificationRequest('/shared-deals', signal); }
  catch (error) {
    // One fresh GET for the observed transient directory outage; never retry
    // authorization refusals, aborted navigation, or sharing mutations.
    if (signal.aborted || !(error instanceof ClassificationRequestError) || error.status !== 503) throw error;
    response = await classificationRequest('/shared-deals', signal);
  }
  const body = await response.json();
  if (!Array.isArray(body.deals) || body.deals.some((d: SharedDeal) => !/^cs_[a-f0-9]{32}$/.test(d.share_id)
    || !/^[a-f0-9]{32}$/.test(d.root_contract_id) || !/^proj_[a-f0-9]{12}$/.test(d.project_id) || typeof d.label !== 'string' || d.access !== 'edit')) throw new Error('Shared deals could not be verified.');
  return body.deals;
}
export function sharedDealLink(projectId: string, root: string, shareId: string) {
  const query = new URLSearchParams({ shared_deal: shareId, page: 'standalone.html', view: 'contract', instrument: `contract:${root}` });
  return `/classification/${encodeURIComponent(projectId)}?${query}`;
}
export async function changeDealSharing(context: ClassificationContext, root: string, recipient: string,
  revision: string | null, remove: boolean, signal: AbortSignal): Promise<DealSharing> {
  const method = remove ? 'DELETE' : 'POST';
  const key = SHARING_MUTATION_PREFIX + await digest(JSON.stringify([...classificationContextScope(context), root, recipient, method, revision]));
  let id = sessionStorage.getItem(key);
  if (!id || !/^[a-f0-9-]{20,64}$/.test(id)) { id = crypto.randomUUID(); sessionStorage.setItem(key, id); }
  const base = await classificationBase();
  const headers = await authHeaders();
  if (!headers.Authorization) throw new Error('Sign in again to change sharing.');
  const path = sharePath(context, root) + (remove ? `/${encodeURIComponent(recipient)}` : '');
  const body = { ...(remove ? {} : { recipient_uid: recipient, access: 'edit' }), expected_revision: revision, mutation_id: id };
  const response = await fetch(`${base}/classification${path}`, { method, signal,
    headers: { ...headers, 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    credentials: 'omit', cache: 'no-store', redirect: 'error' });
  const result = await response.json();
  if (result?.mutation?.id === id && result.mutation.phase === 'rejected') {
    if (sessionStorage.getItem(key) === id) sessionStorage.removeItem(key);
    throw new SharingChangeError('The sharing change was rejected. Check current access before choosing a new change.', true);
  }
  if (!response.ok || result?.mutation?.id !== id || result.mutation.phase !== 'completed') {
    throw new SharingChangeError('Sharing was not confirmed. Check the saved access or explicitly retry this change.');
  }
  const sharing = checked(result.sharing, root);
  if (sessionStorage.getItem(key) === id) sessionStorage.removeItem(key);
  return sharing;
}
